"use client";

/**
 * ToolBadge - Compact status badge for tool displays
 *
 * A standalone, compact version of the status badge that can be used
 * in headers, lists, and other compact contexts.
 */

import type { ReactNode } from "react";
import { Tag } from "@/ui";
import type { TagProps } from "@/ui";
import {
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Circle,
  Sync,
  HourglassEmpty,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { colors, spacing } from "@/ui/tokens";
import type { ToolStatus } from "@/lib/sola/types/tools";

export interface ToolBadgeProps {
  status: ToolStatus;
  size?: "small" | "medium";
  showLabel?: boolean;
  className?: string;
}

/**
 * Human-readable status labels
 */
const STATUS_LABELS: Record<ToolStatus, string> = {
  idle: "Idle",
  "input-streaming": "Preparing",
  "input-ready": "Ready",
  executing: "Running",
  streaming: "Loading",
  complete: "Done",
  error: "Error",
};

/**
 * Status configuration
 */
const STATUS_CONFIG: Record<
  ToolStatus,
  {
    icon: ReactNode;
    color: TagProps["color"];
    dotColor: string;
  }
> = {
  complete: {
    icon: <CheckCircle style={{ fontSize: "inherit" }} />,
    color: "success",
    dotColor: colors.signal.success,
  },
  error: {
    icon: <ErrorIcon style={{ fontSize: "inherit" }} />,
    color: "danger",
    dotColor: colors.signal.danger,
  },
  executing: {
    icon: (
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        style={{ display: "flex", alignItems: "center" }}
      >
        <Sync style={{ fontSize: "inherit" }} />
      </motion.div>
    ),
    color: "warning",
    dotColor: colors.signal.warning,
  },
  streaming: {
    icon: (
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        style={{ display: "flex", alignItems: "center" }}
      >
        <Sync style={{ fontSize: "inherit" }} />
      </motion.div>
    ),
    color: "warning",
    dotColor: colors.signal.warning,
  },
  "input-streaming": {
    icon: (
      <motion.div
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        style={{ display: "flex", alignItems: "center" }}
      >
        <HourglassEmpty style={{ fontSize: "inherit" }} />
      </motion.div>
    ),
    color: "primary",
    dotColor: colors.action.product,
  },
  "input-ready": {
    icon: <AccessTime style={{ fontSize: "inherit" }} />,
    color: "primary",
    dotColor: colors.action.product,
  },
  idle: {
    icon: <Circle style={{ fontSize: "inherit" }} />,
    color: "neutral",
    dotColor: colors.text.muted,
  },
};

/**
 * Compact status badge component
 */
export function ToolBadge({
  status,
  size = "small",
  showLabel = true,
  className,
}: ToolBadgeProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.idle;
  const tagSize = size === "small" ? "sm" : "md";
  const chipHeight = size === "small" ? 22 : 26;

  if (!showLabel) {
    // Icon-only mode
    return (
      <div
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: chipHeight,
          height: chipHeight,
          borderRadius: "50%",
          backgroundColor: colors.action.productLight,
          fontSize: size === "small" ? 14 : 16,
        }}
      >
        {config.icon}
      </div>
    );
  }

  return (
    <Tag
      variant="signal"
      color={config.color}
      size={tagSize}
      icon={config.icon}
      className={className}
    >
      {STATUS_LABELS[status]}
    </Tag>
  );
}

/**
 * Minimal dot indicator for inline use
 */
export function ToolStatusDot({
  status,
  size = 8,
}: {
  status: ToolStatus;
  size?: number;
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.idle;

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: config.dotColor,
        flexShrink: 0,
      }}
    />
  );
}

export default ToolBadge;
